import type { TargetareActivitateFirma } from "@/types/targetare";
import {
  raportImplicit,
  type DefinitieRaport,
  type FiltruRapidDef,
} from "@/utils/targetareRaport";
import {
  ETICHETE_DIMENSIUNE,
  ETICHETE_INCADRARE,
  ETICHETE_NIVEL_RISC,
  ETICHETE_TIER,
  type ColoanaTabel,
} from "@/utils/targetareTabel";
import { ETICHETE_STARE_TINTA, STARI_TINTA } from "@/utils/targetareTinte";

export type RandActivitate = TargetareActivitateFirma & {
  in_univers_text: string;
  tinta_text: string;
};

export const ETICHETE_NIVEL_APLICATIE: Record<string, string> = {
  "de BAZĂ": "De bază",
  IMPORTANT: "Important",
  ESENȚIAL: "Esențial",
};

export function randuriActivitate(
  firme: TargetareActivitateFirma[]
): RandActivitate[] {
  return firme.map((f) => ({
    ...f,
    in_univers_text: f.in_univers
      ? "în univers"
      : f.origine === "nis2pass"
        ? "adăugată din platformă"
        : "în afara universului",
    tinta_text: f.tinta_stare
      ? ETICHETE_STARE_TINTA[f.tinta_stare]
      : "(nu este țintă)",
  }));
}

export const COLOANE_ACTIVITATE: ColoanaTabel<RandActivitate>[] = [
  {
    id: "denumire",
    eticheta: "Firmă",
    tip: "text",
    latime: 240,
    implicit: true,
  },
  { id: "cui", eticheta: "CUI", tip: "text", latime: 100, implicit: true },
  {
    id: "utilizatori",
    eticheta: "Utilizatori",
    tip: "multi",
    latime: 220,
    implicit: true,
  },
  {
    id: "nr_sesiuni",
    eticheta: "Sesiuni",
    tip: "numar",
    latime: 80,
    implicit: true,
  },
  {
    id: "sesiuni_finalizate",
    eticheta: "Finalizate",
    tip: "numar",
    latime: 90,
    implicit: true,
  },
  {
    id: "ultima_activitate",
    eticheta: "Ultima activitate",
    tip: "text",
    latime: 150,
    implicit: true,
  },
  {
    id: "prima_activitate",
    eticheta: "Prima activitate",
    tip: "text",
    latime: 150,
    implicit: false,
  },
  {
    id: "dimensiune",
    eticheta: "Dimensiune declarată",
    tip: "lista",
    latime: 130,
    implicit: true,
  },
  {
    id: "incadrare",
    eticheta: "Încadrare (platformă)",
    tip: "lista",
    latime: 140,
    implicit: true,
    optiuni: ["ESSENTIAL", "IMPORTANT", "OUT_OF_SCOPE"],
    etichete: ETICHETE_INCADRARE,
  },
  {
    id: "incadrare_ro",
    eticheta: "Încadrare (text)",
    tip: "text",
    latime: 160,
    implicit: false,
  },
  {
    id: "nivel_risc",
    eticheta: "Nivel de risc (analiză)",
    tip: "lista",
    latime: 140,
    implicit: true,
    optiuni: ["ESENȚIAL", "IMPORTANT", "de BAZĂ"],
    etichete: ETICHETE_NIVEL_APLICATIE,
  },
  {
    id: "scor_risc",
    eticheta: "Scor risc (analiză)",
    tip: "numar",
    latime: 100,
    implicit: false,
  },
  {
    id: "nr_servicii",
    eticheta: "Servicii selectate",
    tip: "numar",
    latime: 100,
    implicit: false,
  },
  {
    id: "in_univers_text",
    eticheta: "Univers",
    tip: "lista",
    latime: 150,
    implicit: true,
    optiuni: ["în univers", "adăugată din platformă", "în afara universului"],
  },
  {
    id: "tier",
    eticheta: "Tier estimat",
    tip: "lista",
    latime: 90,
    implicit: true,
    optiuni: ["A", "B", "C", "D"],
    etichete: ETICHETE_TIER,
  },
  {
    id: "clasificare_estimata",
    eticheta: "Încadrare estimată",
    tip: "lista",
    latime: 140,
    implicit: true,
    optiuni: ["ESSENTIAL", "IMPORTANT", "OUT_OF_SCOPE"],
    etichete: ETICHETE_INCADRARE,
  },
  {
    id: "nivel_risc_estimat",
    eticheta: "Risc estimat",
    tip: "lista",
    latime: 120,
    implicit: true,
    optiuni: ["esential", "important", "de_baza"],
    etichete: ETICHETE_NIVEL_RISC,
  },
  {
    id: "dimensiune_stricta",
    eticheta: "Dimensiune (bilanț)",
    tip: "lista",
    latime: 110,
    implicit: false,
    optiuni: ["mare", "mijlocie", "mica", "micro"],
    etichete: ETICHETE_DIMENSIUNE,
  },
  {
    id: "sectoare",
    eticheta: "Sectoare",
    tip: "multi",
    latime: 180,
    implicit: false,
  },
  {
    id: "judet",
    eticheta: "Județ",
    tip: "lista",
    latime: 110,
    implicit: false,
  },
  {
    id: "email_principal",
    eticheta: "Email (univers)",
    tip: "text",
    latime: 200,
    implicit: false,
  },
  {
    id: "tinta_text",
    eticheta: "Țintă",
    tip: "lista",
    latime: 150,
    implicit: true,
    optiuni: [
      "(nu este țintă)",
      ...STARI_TINTA.map((s) => ETICHETE_STARE_TINTA[s]),
    ],
  },
];

export const FILTRE_RAPIDE_ACTIVITATE: FiltruRapidDef[] = [
  {
    coloana: "incadrare",
    eticheta: "Încadrare (platformă)",
    optiuni: ["ESSENTIAL", "IMPORTANT", "OUT_OF_SCOPE"],
    etichete: ETICHETE_INCADRARE,
  },
  {
    coloana: "nivel_risc",
    eticheta: "Nivel de risc (analiză)",
    optiuni: ["ESENȚIAL", "IMPORTANT", "de BAZĂ"],
    etichete: ETICHETE_NIVEL_APLICATIE,
  },
  { coloana: "dimensiune", eticheta: "Dimensiune declarată" },
  {
    coloana: "in_univers_text",
    eticheta: "Univers",
    optiuni: ["în univers", "adăugată din platformă", "în afara universului"],
  },
  {
    coloana: "tinta_text",
    eticheta: "Țintă",
    optiuni: [
      "(nu este țintă)",
      ...STARI_TINTA.map((s) => ETICHETE_STARE_TINTA[s]),
    ],
  },
];

const IMPLICITE = COLOANE_ACTIVITATE.filter((c) => c.implicit).map((c) => c.id);

export const definitieRaportActivitate: DefinitieRaport<RandActivitate> = {
  cheie: "targetare.rapoarte.activitate",
  coloane: COLOANE_ACTIVITATE,
  rapoarteImplicite: [
    raportImplicit("Toate firmele evaluate", IMPLICITE, {
      sortare: [{ id: "ultima_activitate", desc: true }],
    }),
    raportImplicit("Cu rezultat de încadrare", IMPLICITE, {
      filtre: [
        {
          id: "f-inc",
          coloana: "incadrare",
          operator: "nu_este_gol",
          activ: true,
        },
      ],
      sortare: [{ id: "ultima_activitate", desc: true }],
    }),
    raportImplicit("Nu sunt încă ținte", IMPLICITE, {
      filtre: [
        {
          id: "f-tinta",
          coloana: "tinta_text",
          operator: "egal",
          valoare: "(nu este țintă)",
          activ: true,
        },
      ],
      sortare: [{ id: "ultima_activitate", desc: true }],
    }),
  ],
  cheieRand: (r) => r.cui,
  filtreRapide: FILTRE_RAPIDE_ACTIVITATE,
  numeFisier: "activitate-nis2pass",
  mesajGol: "Nicio sesiune cu firmă completată pentru filtrele alese.",
};
